import { useState, useEffect, FormEvent } from 'react';
import {
  getMembers,
  createMember,
  updateMember,
  archiveMember,
  Member,
  MemberRole,
  MemberAgeType,
  MEMBER_ROLES,
  ROLE_LABELS,
} from '../api/client';
import TeamNav from '../components/TeamNav';

/**
 * Members tab — the people of the club. A Member is ANY person connected
 * to the club (adults AND children), with identity + an age tag + a set
 * of CRUD-able roles (parent/guardian/coach/admin/payer/player). This is
 * the master list + editor; the Coaches and Guardians tabs are role-lenses
 * over the same records.
 */

const ROLE_FILTERS: Array<{ key: 'all' | MemberRole; label: string }> = [
  { key: 'all', label: 'All' },
  { key: 'guardian', label: 'Parents / guardians' },
  { key: 'coach', label: 'Coaches' },
  { key: 'player', label: 'Players' },
  { key: 'payer', label: 'Payers' },
  { key: 'admin', label: 'Admin staff' },
];

const AGE_TYPES: MemberAgeType[] = ['adult', 'youth', 'child'];

interface FormState {
  name: string;
  email: string;
  phone: string;
  ageType: MemberAgeType;
  roles: MemberRole[];
}

function emptyForm(): FormState {
  return { name: '', email: '', phone: '', ageType: 'adult', roles: [] };
}

function memberToForm(m: Member): FormState {
  return { name: m.name, email: m.email, phone: m.phone, ageType: m.ageType, roles: [...m.roles] };
}

function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  return (parts[0]![0]! + (parts[1]?.[0] || '')).toUpperCase();
}

export default function Members() {
  const [members, setMembers] = useState<Member[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const [roleFilter, setRoleFilter] = useState<'all' | MemberRole>('all');
  const [showArchived, setShowArchived] = useState(false);

  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState<Member | null>(null);
  const [form, setForm] = useState<FormState>(emptyForm());
  const [saving, setSaving] = useState(false);

  useEffect(() => { void load(); }, []);

  async function load() {
    setLoading(true);
    try {
      setMembers(await getMembers());
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load members');
    } finally {
      setLoading(false);
    }
  }

  function openCreate() {
    setEditing(null);
    setForm(emptyForm());
    setShowModal(true);
  }

  function openEdit(m: Member) {
    setEditing(m);
    setForm(memberToForm(m));
    setShowModal(true);
  }

  function closeModal() {
    setShowModal(false);
    setEditing(null);
    setForm(emptyForm());
  }

  function toggleRole(role: MemberRole) {
    setForm(f => ({
      ...f,
      roles: f.roles.includes(role) ? f.roles.filter(r => r !== role) : [...f.roles, role],
    }));
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError('');
    if (!form.name.trim()) { setError('Name is required'); return; }
    try {
      setSaving(true);
      const payload = {
        name: form.name.trim(),
        email: form.email.trim(),
        phone: form.phone.trim(),
        ageType: form.ageType,
        roles: form.roles,
      };
      if (editing) {
        await updateMember(editing.memberId, payload);
        setSuccess(`Updated ${form.name.trim()}`);
      } else {
        await createMember(payload);
        setSuccess(`Added ${form.name.trim()}`);
      }
      await load();
      closeModal();
      setTimeout(() => setSuccess(''), 3000);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to save member');
    } finally {
      setSaving(false);
    }
  }

  async function handleArchive(m: Member) {
    if (!confirm(`Archive ${m.name}? They'll be hidden from the lists but their record is kept.`)) return;
    try {
      await archiveMember(m.memberId);
      setSuccess(`${m.name} archived`);
      await load();
      setTimeout(() => setSuccess(''), 3000);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to archive member');
    }
  }

  const visible = members
    .filter(m => showArchived || m.status !== 'archived')
    .filter(m => roleFilter === 'all' || m.roles.includes(roleFilter));

  const emails = visible.map(m => m.email).filter(Boolean);

  return (
    <div className="tm-page-shell">
      <TeamNav />

      <div className="page-head">
        <div>
          <h1>Members</h1>
          <p className="subtitle">
            Everyone connected to your club — adults and children. Give each person
            their roles (a coach can also be a paying parent). Players keep their
            squad profile on the Players tab.
          </p>
        </div>
        <div className="head-actions">
          {emails.length > 0 && (
            <a className="ebtn ebtn-secondary" href={`mailto:?bcc=${encodeURIComponent(emails.join(','))}`}>
              Email all ({emails.length})
            </a>
          )}
          <button className="ebtn ebtn-primary" onClick={openCreate}>+ Add member</button>
        </div>
      </div>

      {error && <div className="ealert ealert-error">{error}</div>}
      {success && <div className="ealert ealert-success">{success}</div>}

      <div className="tm-filters">
        {ROLE_FILTERS.map(f => (
          <button
            key={f.key}
            className={`tm-filter ${roleFilter === f.key ? 'active' : ''}`}
            onClick={() => setRoleFilter(f.key)}
          >
            {f.label}
          </button>
        ))}
        <span className="tm-filter-sep" />
        <label className="tm-filter" style={{ cursor: 'pointer' }}>
          <input
            type="checkbox"
            checked={showArchived}
            onChange={e => setShowArchived(e.target.checked)}
            style={{ marginRight: 6 }}
          />
          Show archived
        </label>
      </div>

      {loading ? (
        <p className="loading">Loading members…</p>
      ) : visible.length === 0 ? (
        <div className="empty-state">
          <p>No members{roleFilter !== 'all' ? ' with this role' : ''} yet.</p>
          <button className="ebtn ebtn-primary" style={{ marginTop: 12 }} onClick={openCreate}>+ Add member</button>
        </div>
      ) : (
        <div className="card-surface">
          <table className="tm-table">
            <thead>
              <tr>
                <th>Member</th>
                <th>Roles</th>
                <th>Type</th>
                <th>Phone</th>
                <th aria-label="Actions" />
              </tr>
            </thead>
            <tbody>
              {visible.map(m => (
                <tr key={m.memberId} className={m.status === 'archived' ? 'pt-archived' : ''}>
                  <td>
                    <div className="tm-person">
                      {m.image ? (
                        <img className="tm-avatar" src={m.image} alt="" />
                      ) : (
                        <span className="tm-avatar tm-avatar-empty">{initials(m.name)}</span>
                      )}
                      <div>
                        <div className="tm-person-name">{m.name || '(no name)'}</div>
                        {m.email && <a className="tm-link" href={`mailto:${m.email}`}>{m.email}</a>}
                      </div>
                    </div>
                  </td>
                  <td>
                    <div className="tm-chips">
                      {m.roles.length === 0
                        ? <span className="tm-muted">—</span>
                        : m.roles.map(r => <span key={r} className="tm-chip">{ROLE_LABELS[r] ?? r}</span>)}
                    </div>
                  </td>
                  <td style={{ textTransform: 'capitalize' }}>{m.ageType}</td>
                  <td>{m.phone || <span className="tm-muted">—</span>}</td>
                  <td>
                    <div className="tm-actions">
                      <button className="ebtn ebtn-secondary ebtn-sm" onClick={() => openEdit(m)}>Edit</button>
                      {m.status !== 'archived' && (
                        <button className="ebtn ebtn-danger ebtn-sm" onClick={() => handleArchive(m)}>Archive</button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showModal && (
        <div className="tm-modal-overlay" onClick={closeModal}>
          <div className="tm-modal" onClick={e => e.stopPropagation()}>
            <div className="tm-modal-header">
              <h2>{editing ? 'Edit member' : 'Add member'}</h2>
              <button type="button" className="tm-modal-close" onClick={closeModal}>×</button>
            </div>
            <form onSubmit={handleSubmit}>
              <div className="tm-modal-body">
                <label className="tm-field">
                  <span>Name *</span>
                  <input type="text" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} required />
                </label>
                <div className="tm-form-row">
                  <label className="tm-field">
                    <span>Email</span>
                    <input type="email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} />
                  </label>
                  <label className="tm-field">
                    <span>Phone</span>
                    <input type="tel" value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })} />
                  </label>
                </div>
                <label className="tm-field">
                  <span>Type</span>
                  <select value={form.ageType} onChange={e => setForm({ ...form, ageType: e.target.value as MemberAgeType })}>
                    {AGE_TYPES.map(a => <option key={a} value={a}>{a.charAt(0).toUpperCase() + a.slice(1)}</option>)}
                  </select>
                </label>
                <div className="tm-field">
                  <span>Roles</span>
                  <div className="tm-roles">
                    {MEMBER_ROLES.map(r => (
                      <label key={r}>
                        <input type="checkbox" checked={form.roles.includes(r)} onChange={() => toggleRole(r)} />
                        {ROLE_LABELS[r] ?? r}
                      </label>
                    ))}
                  </div>
                  <small>A person can hold several roles. Coaches are best managed on the Coaches tab (it assigns teams).</small>
                </div>
              </div>
              <div className="tm-modal-actions">
                <button type="button" className="ebtn ebtn-secondary" onClick={closeModal}>Cancel</button>
                <button type="submit" className="ebtn ebtn-primary" disabled={saving}>
                  {saving ? 'Saving…' : editing ? 'Save changes' : 'Add member'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
